import React, { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import styles from "./collectionDetail.module.css";
import FormDialog from "../common/FormDialog";
import { Button, Grid } from "@mui/material";
import { Article } from "@mui/icons-material";
import { isSuperAdmin } from "../../util/form/form";
import EditCollectionData from "./EditCollectionData";
import {
    OpenSnackBarWithResultFail,
    OpenSnackBarWithResultSuccess,
} from "../../redux/common/snackBar/thunk";
import {
    CollectionDetail as CollectionDetailInterface,
    CollectionPhotoList,
} from "../../redux/collection/state";
import {
    ChangeCollectionPhotoListThunk,
    initSelectedEditionCollectionDetailThunk,
    ChangeCollectionDetailThunk,
    ChangeSignatureImageThunk,
    initCollectionAttendanceData,
} from "../../redux/collection/thunk";
import { Moment } from "moment";
import { axiosPostWithFormData, axiosPutWithJson } from "../../util/axios";
import axios from "axios";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";

function CollectionDetail(props: {
    remark: string;
    collectionDate: Date | Moment | null;
    collectionId: number;
}) {
    const [photoDialog, setPhotoDialog] = useState<boolean>(false);
    const [editDialog, setEditDialog] = useState<boolean>(false);
    const [collectionImageDialog, setCollectionImageDialog] =
        useState<boolean>(false);
    const { user } = useSelector((state: IRootState) => state.userAuth);
    const [showPhoto, setShowPhoto] = useState<any>("");
    const isSuperAdminRole = isSuperAdmin(user?.token?.role_id as number);
    const dispatch = useDispatch<IRootThunkDispatch>();
    const {
        collectionDetail,
        signature_image,
        signature_image_thumb,
        collectionPhotoList,
        selectedEditCollectionDetail,
        attendanceCollectionInfo,
    } = useSelector((state: IRootState) => state.collectionState);

    function handleClickEditCollectionDetailButton(
        data: CollectionDetailInterface
    ) {
        setEditDialog(true);
        dispatch(initSelectedEditionCollectionDetailThunk(data));
    }

    const columns: GridColDef[] = [
        {
            field: "collection_response_id",
            headerName: "詳細收集編號",
            flex: 1,
        },
        { field: "recyclable", headerName: "回收類型", flex: 1 },
        { field: "container", headerName: "回收容器", flex: 1 },
        {
            field: "collection_weight",
            headerName: "總重量",
            flex: 1,
            renderCell: (data) => <div>{Number(data.value).toFixed(1)}</div>,
        },
        {
            field: "actual_container_weight",
            headerName: "容器重量",
            flex: 1,
            renderCell: (data) => <div>{Number(data.value).toFixed(1)}</div>,
        },
        {
            field: "net_weight",
            headerName: "淨重量",
            flex: 1,
            renderCell: (data) => <div>{Number(data.value).toFixed(1)}</div>,
        },
        {
            field: "edit",
            headerName: "更改",
            flex: 1,
            renderCell: (data) => (
                <div>
                    <Button
                        variant="contained"
                        onClick={() =>
                            handleClickEditCollectionDetailButton(data.row)
                        }
                        className={styles.editBtn}
                    >
                        <Article />
                        <Grid>更改</Grid>
                    </Button>
                </div>
            ),
        },
    ];

    function handleClickPhoto() {
        setPhotoDialog(true);
    }

    function handleCloseDialog() {
        setPhotoDialog(false);
        setCollectionImageDialog(false);
        setEditDialog(false);
    }

    function handleCollectionPhoto(index: number) {
        if (collectionPhotoList.length > 0) {
            setShowPhoto(collectionPhotoList[index].image);
            setCollectionImageDialog(true);
            return;
        }
        return;
    }

    async function handleEditDetailData() {
        dispatch(OpenLoadingBarThunk(true));
        const result = await axiosPutWithJson(
            `/api/collection/response/${selectedEditCollectionDetail?.collection_response_id}`,
            selectedEditCollectionDetail
        );
        if (result.data.success) {
            dispatch(OpenLoadingBarThunk(false));
            dispatch(OpenSnackBarWithResultSuccess(result.data.message));
            dispatch(
                ChangeCollectionDetailThunk(
                    selectedEditCollectionDetail,
                    collectionDetail!
                )
            );
            setEditDialog(false);
        } else {
            dispatch(OpenSnackBarWithResultFail("無法更改收集數據"));
        }
    }

    async function handleAddCollectionPhoto(
        event: ChangeEvent<HTMLInputElement>
    ) {
        const files = event.target.files;
        if (files?.length === 0) return;
        const form = new FormData();
        form.append("collection_date", props.collectionDate?.toString() ?? "");
        const file = files![0];

        form.append("collection_image", file);
        dispatch(OpenLoadingBarThunk(true));
        const res = await axiosPostWithFormData(
            "api/collection/attendacePhoto/" + props.collectionId,
            form
        );
        if (res.data.success && res.data.record && res.data.record.length) {
            const collectionPhotoListTmp = JSON.parse(
                JSON.stringify(collectionPhotoList)
            );
            collectionPhotoListTmp.push(res.data.record[0]);
            dispatch(ChangeCollectionPhotoListThunk(collectionPhotoListTmp));
        }

        dispatch(OpenLoadingBarThunk(false));
    }

    async function handleAddSignautrePhoto(
        event: ChangeEvent<HTMLInputElement>
    ) {
        const files = event.target.files;
        if (files?.length === 0) return;
        const form = new FormData();
        form.append("collection_date", props.collectionDate?.toString() ?? "");
        const file = files![0];
        const signature_imageBlob = URL.createObjectURL(file);
        dispatch(ChangeSignatureImageThunk(signature_imageBlob));

        const newAttendanceCollectionInfo = attendanceCollectionInfo.map(
            (collectionInfo) => {
                if (collectionInfo.collection_id === props.collectionId) {
                    collectionInfo.signature_image = signature_imageBlob!;
                    collectionInfo.signature_image_thumb = signature_imageBlob!;
                    return collectionInfo;
                } else {
                    return collectionInfo;
                }
            }
        );
        dispatch(initCollectionAttendanceData(newAttendanceCollectionInfo));

        form.append("signature_image", file);
        const res = await axiosPostWithFormData(
            "api/collection/signature/" + props.collectionId,
            form
        );
        if (res.data.success) {
            dispatch(OpenSnackBarWithResultSuccess(res.data.message));
        }
    }

    async function handleDeleteCollectionPhoto(photo: CollectionPhotoList) {
        const token = localStorage.getItem("loginToken");
        dispatch(OpenLoadingBarThunk(true));
        const res = await axios.delete(
            process.env.REACT_APP_API +
                "api/collection/attendacePhoto/" +
                photo.collection_image_id,
            {
                headers: {
                    ...{
                        Authorization: `Bearer ${token}`,
                    },
                },
            }
        );
        if (res.data.success) {
            let collectionPhotoListTmp = JSON.parse(
                JSON.stringify(collectionPhotoList)
            );
            collectionPhotoListTmp = collectionPhotoListTmp.filter(
                (obj: CollectionPhotoList) =>
                    obj.collection_image_id !== photo.collection_image_id
            );
            dispatch(ChangeCollectionPhotoListThunk(collectionPhotoListTmp));
        }
        dispatch(OpenLoadingBarThunk(false));
    }

    return (
        <div>
            {collectionDetail?.length && collectionDetail?.length > 1 ? (
                <div style={{ height: "100%", width: "100%" }}>
                    <DataGrid
                        rows={collectionDetail || []}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 30 },
                            },
                        }}
                        pageSizeOptions={[5, 10]}
                        checkboxSelection
                        getRowId={(row) => row.collection_response_id}
                        columnVisibilityModel={
                            !isSuperAdminRole
                                ? {
                                      edit: false,
                                  }
                                : {}
                        }
                    />
                </div>
            ) : (
                <></>
            )}

            <label htmlFor="photo-upload-button">
                {isSuperAdminRole && (
                    <>
                        <input
                            accept="image/*"
                            id="signature-upload-button"
                            type="file"
                            style={{ display: "none" }}
                            multiple
                            onChange={handleAddSignautrePhoto}
                        />
                        <label htmlFor="signature-upload-button">
                            <Button variant="contained" component="span">
                                上載簽署
                            </Button>
                        </label>
                    </>
                )}
            </label>

            {signature_image ? (
                <div className={styles.signaureContainer}>
                    學校簽署(點擊圖片放大)
                    <img
                        src={
                            signature_image_thumb?.startsWith("blob")
                                ? signature_image_thumb
                                : process.env.REACT_APP_S3 +
                                  signature_image_thumb!
                        }
                        width={"100%"}
                        height={"100%"}
                        style={{ objectFit: "cover", cursor: "pointer" }}
                        onClick={handleClickPhoto}
                    ></img>
                </div>
            ) : (
                <Grid>
                    <Grid>沒有簽署原因：</Grid>
                    <Grid>{props.remark}</Grid>
                </Grid>
            )}

            <FormDialog
                title="放大簽名"
                open={photoDialog}
                handleClose={handleCloseDialog}
                size="lg"
            >
                <img
                    src={
                        signature_image?.startsWith("blob")
                            ? signature_image
                            : process.env.REACT_APP_S3 + signature_image!
                    }
                    width={"100%"}
                    height={"100%"}
                    style={{ objectFit: "cover" }}
                    onClick={handleClickPhoto}
                ></img>
            </FormDialog>

            <Grid>
                <Grid
                    style={{
                        marginBottom: "10px",
                        marginTop: "10px",
                    }}
                    key={`collection_image-title`}
                >
                    收集相片(點擊圖片放大)
                    {isSuperAdminRole && (
                        <>
                            <input
                                accept="image/*"
                                id="photo-upload-button"
                                type="file"
                                style={{ display: "none" }}
                                multiple
                                onChange={handleAddCollectionPhoto}
                            />
                            <label htmlFor="photo-upload-button">
                                <Button variant="contained" component="span">
                                    添加相片
                                </Button>
                            </label>
                        </>
                    )}
                </Grid>
                {collectionPhotoList.length > 0 ? (
                    <>
                        {collectionPhotoList.map((photo, index) => (
                            <>
                                <Grid
                                    key={`collection_image-${index}`}
                                    className={
                                        styles.collectionPhotoOuterContainer
                                    }
                                >
                                    <img
                                        src={`${process.env.REACT_APP_S3}${photo.image}`}
                                        width={"100%"}
                                        height={"100%"}
                                        style={{
                                            objectFit: "cover",
                                            cursor: "pointer",
                                        }}
                                        onClick={() =>
                                            handleCollectionPhoto(index)
                                        }
                                    ></img>
                                </Grid>
                                {isSuperAdminRole && (
                                    <Button
                                        variant="contained"
                                        onClick={() =>
                                            handleDeleteCollectionPhoto(photo)
                                        }
                                        style={{
                                            marginTop: "10px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        刪除照片
                                    </Button>
                                )}
                            </>
                        ))}
                    </>
                ) : (
                    <></>
                )}
            </Grid>

            <FormDialog
                title="收集相片"
                open={collectionImageDialog}
                handleClose={handleCloseDialog}
                size="lg"
            >
                <img
                    src={`${process.env.REACT_APP_S3}${showPhoto}`}
                    width={"100%"}
                    height={"100%"}
                ></img>
            </FormDialog>

            <FormDialog
                title="更改收集紀錄"
                open={editDialog}
                handleClose={handleCloseDialog}
                size="lg"
                handleSubmit={handleEditDetailData}
            >
                <EditCollectionData />
            </FormDialog>
        </div>
    );
}

export default CollectionDetail;
